'use client';

import { UserContactInfo } from "@/components/Types";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/modal";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { setShowAddingContactModal } from "@/store/reducers/appSlice";
import { useEffect, useState } from "react";
import { api } from "@/lib/api-requests";
import { useUserLocale } from "@/hooks/useUserLocale";
import AvatarWithAnimatedCircle from "@/components/AvatarWithAnimatedCircle";
import { Spinner } from "@heroui/spinner";
import { EventQueueItem } from "@/providers/ServiceModalsProviderEventQueueItem";
import { appTutorialService } from "@/lib/services/TutorialService";

export interface AddingContactModalProps {
    onClose?: () => void;
    event?: EventQueueItem;
}

export default function AddingContactModal({ onClose, event }: AddingContactModalProps) {
    const { t } = useUserLocale();
    const contact = useSelector((state: RootState) => state.app.addingContact) as UserContactInfo | null;
    const currentUser = useSelector((state: RootState) => state.app.currentUser);
    const showAddingContactModal = useSelector((state: RootState) => state.app.showAddingContactModal);
    const dispatch = useDispatch();

    // Hide the modal after a bit
    useEffect(() => {
        let timeout: NodeJS.Timeout | null = null;

        if (showAddingContactModal) {
            timeout = setTimeout(() => {
                dispatch(setShowAddingContactModal({ show: false, contact: null }));
                onClose?.();
            }, 2300);

            appTutorialService.queueMessage({
                fragmentId: 'firstContact1',
                language: currentUser?.motherLanguage,
                modalId: 'AddingContactModal',
            });
        
            appTutorialService.queueMessage({
                fragmentId: 'firstContact2',
                language: currentUser?.motherLanguage,
                modalId: 'AddingContactModal',
            });
        }

        return () => timeout && clearTimeout(timeout);
    }, [showAddingContactModal]);

    return (
        <Modal isDismissable={false} backdrop={"transparent"} shadow="lg" closeButton={<></>} isOpen={showAddingContactModal} onClose={() => dispatch(setShowAddingContactModal({ show: false, contact: null }))}>
            {contact && <ModalContent>
                <ModalHeader className="flex flex-col items-center gap-2">
                    <h1>{t.feed.addingContact.title}</h1>
                </ModalHeader>
                <ModalBody className="pb-6">
                    <div className="flex flex-col items-center gap-4">
                        <AvatarWithAnimatedCircle imageSrc={contact?.avatarUrl} alt={contact?.defaultName} size='lg' />
                        <div className="text-lg font-semibold text-center">{contact?.defaultName}</div>
                        <div className="flex justify-center items-center">
                            <Spinner color="primary" size="lg" />
                        </div>
                        <div className="text-sm text-center p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-500 rounded-lg shadow-md">{contact?.tagline || contact?.alias}</div>
                    </div>
                </ModalBody>                
            </ModalContent>}
        </Modal>
    );
}